import React, {useState, useEffect} from "react";
import {TextField, RadioGroup} from "formik-material-ui";
import {
    Typography,
    Grid,
    FormControlLabel,
    TextField as RTextField,
    Button,
    makeStyles,
    Radio,
    Container, Box, CircularProgress
} from "@material-ui/core";
import {Formik, Form, Field, ErrorMessage} from "formik";
import {colorError} from "../../../Constants/theme";
import * as Yup from "yup";
import {Autocomplete} from "@material-ui/lab";
import {insurances, insuranceList} from "../../../Constants/insurance";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";
import {editAnimal} from "../../../redux/pets/pets.actions";
import {petSex} from "../../../interfaces";
import {useSnackbar} from "notistack";
import API from "../../../helpers/apiHelpers";

interface Props {
    changeCount: (number: number) => void;
    isWhiteLabel: boolean;
    pet: any;
    setPet: React.Dispatch<React.SetStateAction<{
        id: number;
        ownerId: number;
        type: string;
        name: string;
        breed: string;
        weight: number | undefined;
        sex: string;
        neuteredSpayed: boolean;
        vaccines: any[];
        medications: any[];
        allergies: any[];
        chronicConditions: any[];
        dateOfBirth: string;
        imageUrl: string;
    }>>;
    petId: number;
    setPetId: React.Dispatch<React.SetStateAction<number>>;
    isOnBoarding: boolean;
}

interface Props {
    changeCount: (number: number) => void;
    isWhiteLabel: boolean;
    pet: any;
    setPet: React.Dispatch<React.SetStateAction<{
        id: number;
        ownerId: number;
        type: string;
        name: string;
        breed: string;
        weight: number;
        sex: string;
        neuteredSpayed: boolean;
        vaccines: any[];
        medications: any[];
        allergies: any[];
        chronicConditions: any[];
        dateOfBirth: string;
        imageUrl: string;
    }>>;
    petId: number;
    setPetId: React.Dispatch<React.SetStateAction<number>>;
}

const useStyle = makeStyles(() => ({
    body: {
        paddingTop: 20,
    },
    cancelButton: {
        border: `1.5px solid ${colorError}`,
        color: colorError
    },
    spacing: {
        paddingTop: 20,
    },
    formError: {
        color: colorError,
        fontSize: "12px"
    }
}));

const PetDetails = ({changeCount, petId, setPetId, pet, setPet, isWhiteLabel, isOnBoarding}: Props) => {
    const classes = useStyle();
    const {enqueueSnackbar} = useSnackbar();
    const dispatch = useAppDispatch();
    const user = useSelector((state: RootState) => state?.user);
    const clinicName = useSelector((state: RootState) => state?.branding.name);
    const [pcv, setPCV] = useState(clinicName ? "yes" : "no");
    const [clinic, setClinic] = useState(clinicName);
    const [pcvPhone, setPCVPhone] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const branding = useSelector((state: RootState) => state?.branding);
    const pets = useSelector((state: RootState) => state?.pets.petsList);
    const partnerType = useSelector((state: RootState) => state?.branding.type);
    const validationSchema = Yup.object().shape({
        sex: Yup.string().required("Required"),
        neuteredSpayed: Yup.boolean().required("Required"),
        clinicEmail: Yup.string().email("Invalid email")
    });
    const [formikValues, setFormikValues] = useState({
        sex: "",
        neuteredSpayed: "",
        insuranceProvider: "",
        otherInsuranceProvider: "",
        clinicEmail: ""
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!pet) {
            (async () => {
                let newPet = await API.getAnimalById(petId);
                setPet(newPet);
            })();
        }
        if (petId && pet) {
            if (petId === 0 || isOnBoarding) {
                return;
            }
            setFormikValues({
                sex: petId !== 0 ? pet.sex as petSex : "",
                neuteredSpayed: pet.neuteredSpayed === true ? "true" : pet.neuteredSpayed === false ? "false" : "",
                insuranceProvider: pet.hasOwnProperty("insuranceProvider") ? insurances.filter(item => item.value === pet["insuranceProvider"])[0].label : "",
                otherInsuranceProvider: pet.hasOwnProperty("otherInsuranceProvider") ? pet["otherInsuranceProvider"] : "",
                clinicEmail: pet?.clinicEmail,
            });
            if (Object.prototype.hasOwnProperty.call(pet,"primaryVetName")){
                setClinic(pet["primaryVetName"] || clinicName);
                if (pet["primaryVetName"] !== "" || clinicName.length){
                    setPCV("yes");
                }else{
                    setPCV("no");
                }
            }
            if (Object.prototype.hasOwnProperty.call(pet,"primaryVetPhoneNumber")){setPCVPhone(pet["primaryVetPhoneNumber"]);}
        }
    }, [petId]);

    const handlePCVChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPCV((event.target as HTMLInputElement).value);
    };

    return (
        <Container className={classes.body} disableGutters={true}>
            <Typography variant="h2">
                Your Pet
            </Typography>
            { isLoading ?
                <Box width="100%" height="300px" display="flex" justifyContent="center" alignItems="center">
                    <CircularProgress/>
                </Box>
                :
                <Formik
                    initialValues={formikValues}
                    enableReinitialize
                    onSubmit={async (values) => {
                        let neuteredSpayed;
                        if (values.neuteredSpayed === "true") {
                            neuteredSpayed = true;
                        } else {
                            neuteredSpayed = false;
                        }
                        try {
                            setIsLoading(true);
                            const userInsurance = values.insuranceProvider !== "" ? insurances.filter(item => item.label === values.insuranceProvider)[0].value : null;
                            const otherInsurance = values.otherInsuranceProvider !== "" ? values.otherInsuranceProvider : "";
                            const body = {
                                ownerId: user.userId,
                                sex: values.sex,
                                neuteredSpayed: neuteredSpayed,
                                insuranceProvider: userInsurance,
                                otherInsuranceProvider: otherInsurance,
                                clinicEmail: values.clinicEmail,
                                ...pet
                            };
                            if (pcv === "yes"){
                                body["primaryVetName"] = clinic;
                                body["primaryVetPhoneNumber"] = pcvPhone;
                            }else{
                                body["primaryVetName"] = "";
                                body["primaryVetPhoneNumber"] = "";
                            }
                            delete body.id;
                            delete body.created;
                            delete body.imageUrl;
                            await dispatch(editAnimal(petId, body));
                            changeCount(1);
                        } catch (error) {
                            setIsLoading(false);
                            enqueueSnackbar(error.message || error.errorMessage, {
                                variant: "error",
                                autoHideDuration: 3000,
                            });
                        }
                    }}
                    validationSchema={validationSchema}
                >
                    {({isSubmitting, errors, values, handleChange, setFieldValue}) => (
                        <Form>
                            <Grid container spacing={2} className={classes.spacing} alignItems="center">
                                <Grid item container alignItems="center" xs={12}>
                                    <Grid item xs={3} sm={3}>
                                        <Typography variant="body2">
                                            Sex
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={9} sm={9}>
                                        <Field component={RadioGroup} name="sex" value={values.sex} row>
                                            <FormControlLabel value="MALE" control={<Radio/>} label="Male"/>
                                            <FormControlLabel value="FEMALE" control={<Radio/>} label="Female"/>
                                        </Field>
                                        <ErrorMessage name="sex">
                                            {msg => <Typography className={classes.formError}>{msg}</Typography>}
                                        </ErrorMessage>
                                    </Grid>
                                </Grid>
                                <Grid item container alignItems="center" xs={12}>
                                    <Grid item xs={4} sm={3}>
                                        <Typography variant="body2">
                                            {values.sex === "FEMALE" ? "Spayed" : "Neutered"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8} sm={9}>
                                        <Field component={RadioGroup} name="neuteredSpayed" value={values.neuteredSpayed}
                                            onChange={handleChange} row>
                                            <FormControlLabel value="true" control={<Radio/>} label="Yes"/>
                                            <FormControlLabel value="false" control={<Radio/>} label="No"/>
                                        </Field>
                                        <ErrorMessage name="neuteredSpayed">
                                            {msg => <Typography className={classes.formError}>{msg}</Typography>}
                                        </ErrorMessage>
                                    </Grid>
                                </Grid>
                                <Grid item container alignItems="center" xs={12}>
                                    <Grid item xs={12} sm={3}>
                                        <Typography variant="body2">
                                        Pet Insurance <span
                                                style={{fontWeight: 400, fontSize: "12px"}}>(If you have it)</span>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9}>
                                        <Autocomplete
                                            fullWidth
                                            options={insuranceList}
                                            value={values.insuranceProvider}
                                            getOptionLabel={(option) => option}
                                            onChange={(event, value) => {
                                                value ? setFieldValue("insuranceProvider", value) : setFieldValue("insuranceProvider", "");
                                            }}

                                            renderInput={(params) => <RTextField {...params} margin="dense"
                                                variant="outlined"/>}
                                        />
                                    </Grid>
                                </Grid>
                                {values.insuranceProvider === "Other" &&
                            <Grid item container alignItems="center" xs={12}>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="body2">
                                        Add your insurance
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={9}>
                                    <Field
                                        component={TextField}
                                        name="otherInsuranceProvider"
                                        value={values.otherInsuranceProvider}
                                        variant="outlined"
                                        color="secondary"
                                        type="text"
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        onChange={handleChange}
                                        placeholder=""

                                    />
                                </Grid>
                            </Grid>
                                }
                            </Grid>
                            <Grid item xs={12} container alignItems="center">
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="body2">
                                    Primary Care Vet
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={9}>
                                    {/*@ts-ignore*/}
                                    <RadioGroup name="pcv" value={pcv} row onChange={handlePCVChange}>
                                        <FormControlLabel value="yes" disabled={clinicName ? true : false} control={<Radio/>} label="Yes"/>
                                        <FormControlLabel value="no" disabled={clinicName ? true : false} control={<Radio/>} label="No"/>
                                    </RadioGroup>
                                </Grid>
                                <ErrorMessage name="pcv">
                                    {msg => <Typography className={classes.formError}>{msg}</Typography>}
                                </ErrorMessage>
                            </Grid>
                            {pcv === "yes" &&
                        <Grid item xs={12} container alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <Typography variant="body2">
                                    Clinic Name
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <RTextField
                                    value={clinic}
                                    onChange={(e) => setClinic(e.target.value)}
                                    variant="outlined"
                                    color="secondary"
                                    type="text"
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    disabled={partnerType === "CLINIC"}
                                    name="clinic"
                                />
                            </Grid>
                            {!clinicName && <>
                                <Grid item xs={12} sm={3} style={{marginTop: 10}}>
                                    <Typography variant="body2">
                                        Phone Number
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={9} style={{marginTop: 10}}>
                                    <RTextField
                                        value={pcvPhone}
                                        onChange={(e) => setPCVPhone(e.target.value)}
                                        variant="outlined"
                                        color="secondary"
                                        type="text"
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name="phoneNumber"
                                    />
                                </Grid>
                                <Grid item container alignItems="center" xs={12}>
                                    <Grid item xs={12} sm={3}>
                                        <Typography variant="body2">
                                            Clinic Email
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9}>
                                        <Field
                                            component={TextField}
                                            fullWidth
                                            onChange={handleChange}
                                            value={values.clinicEmail}
                                            name="clinicEmail"
                                            // inputProps={{min: 0, step: 0.1}}
                                            type="text"
                                            variant="outlined"
                                            color="secondary"
                                            margin="dense"
                                        />
                                    </Grid>
                                </Grid>
                            </>}
                        </Grid>
                            }
                            <Grid container justify="flex-start" spacing={2} className={classes.spacing}>
                                <Grid item xs={12} sm={3}>
                                    <Button
                                        fullWidth
                                        color="secondary"
                                        variant="outlined"
                                        type="submit"
                                        data-testid="submitButton"
                                    >
                                    Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>}
        </Container>
    );
};

export default PetDetails;
